import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  Textarea,
  VStack,
  useToast,
  FormErrorMessage,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { axiosInstance } from '../../Utilities/networks';

const EnquiryForm = () => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters')
      .required('Name is required'),
    address: Yup.string()
      .min(10, 'Address must be at least 10 characters')
      .max(500, 'Address must be less than 500 characters')
      .required('Address is required'),
    requirement: Yup.string()
      .min(20, 'Please provide detailed requirements (at least 20 characters)')
      .max(1000, 'Requirements must be less than 1000 characters')
      .required('Requirement details are required'),
    contact_number: Yup.string()
      .matches(/^[\+]?[1-9][\d]{0,15}$/, 'Please enter a valid phone number')
      .required('Contact number is required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    gst_no: Yup.string()
      .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Please enter a valid GST number (15 characters)')
      .optional(),
  });

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true);

    try {
      const response = await axiosInstance.post('/api/sales/enquiries', values);

      if (response.data.status === 'success') {
        toast({
          title: 'Enquiry Submitted Successfully!',
          description: 'We will get back to you soon.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        resetForm();
      } else {
        throw new Error(response.data.message || 'Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);

      toast({
        title: 'Submission Failed',
        description: error.response?.data?.message || error.message || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="4xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Heading size="lg" color="blue.600" mb={2}>
            Customer Enquiry Form
          </Heading>
          <Text color="gray.600">
            Please fill in your details and requirements. We'll get back to you within 24 hours.
          </Text>
        </Box>

        <Card shadow="lg" borderRadius="xl">
          <CardHeader pb={4}>
            <Heading size="md" color="gray.700">
              Contact Information
            </Heading>
          </CardHeader>
          <CardBody>
            <Formik
              initialValues={{
                name: '',
                address: '',
                requirement: '',
                contact_number: '',
                email: '',
                gst_no: '',
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, values, setFieldValue }) => (
                <Form>
                  <VStack spacing={6}>
                    {/* Name Field */}
                    <FormControl isInvalid={errors.name && touched.name}>
                      <FormLabel htmlFor="name" fontWeight="semibold">
                        Full Name *
                      </FormLabel>
                      <Field
                        as={Input}
                        id="name"
                        name="name"
                        placeholder="Enter your full name"
                        size="lg"
                        focusBorderColor="blue.400"
                      />
                      <FormErrorMessage>{errors.name}</FormErrorMessage>
                    </FormControl>

                    {/* Contact Number Field */}
                    <FormControl isInvalid={errors.contact_number && touched.contact_number}>
                      <FormLabel htmlFor="contact_number" fontWeight="semibold">
                        Contact Number *
                      </FormLabel>
                      <Field
                        as={Input}
                        id="contact_number"
                        name="contact_number"
                        placeholder="Enter your contact number"
                        size="lg"
                        focusBorderColor="blue.400"
                      />
                      <FormErrorMessage>{errors.contact_number}</FormErrorMessage>
                    </FormControl>

                    {/* Email Field */}
                    <FormControl isInvalid={errors.email && touched.email}>
                      <FormLabel htmlFor="email" fontWeight="semibold">
                        Email Address *
                      </FormLabel>
                      <Field
                        as={Input}
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email address"
                        size="lg"
                        focusBorderColor="blue.400"
                      />
                      <FormErrorMessage>{errors.email}</FormErrorMessage>
                    </FormControl>

                    {/* GST Number Field (Optional) */}
                    <FormControl isInvalid={errors.gst_no && touched.gst_no}>
                      <FormLabel htmlFor="gst_no" fontWeight="semibold">
                        GST Number (Optional)
                      </FormLabel>
                      <Field
                        as={Input}
                        id="gst_no"
                        name="gst_no"
                        placeholder="Enter your GST number (15 characters)"
                        size="lg"
                        focusBorderColor="blue.400"
                        maxLength={15}
                      />
                      <FormErrorMessage>{errors.gst_no}</FormErrorMessage>
                    </FormControl>

                    {/* Address Field */}
                    <FormControl isInvalid={errors.address && touched.address}>
                      <FormLabel htmlFor="address" fontWeight="semibold">
                        Address *
                      </FormLabel>
                      <Field
                        as={Textarea}
                        id="address"
                        name="address"
                        placeholder="Enter your complete address"
                        size="lg"
                        rows={3}
                        resize="vertical"
                        focusBorderColor="blue.400"
                      />
                      <FormErrorMessage>{errors.address}</FormErrorMessage>
                    </FormControl>

                    {/* Requirements Field */}
                    <FormControl isInvalid={errors.requirement && touched.requirement}>
                      <FormLabel htmlFor="requirement" fontWeight="semibold">
                        Requirements *
                      </FormLabel>
                      <Field
                        as={Textarea}
                        id="requirement"
                        name="requirement"
                        placeholder="Please provide detailed requirements, specifications, quantity, timeline, etc."
                        size="lg"
                        rows={5}
                        resize="vertical"
                        focusBorderColor="blue.400"
                      />
                      <FormErrorMessage>{errors.requirement}</FormErrorMessage>
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        {values.requirement.length}/1000 characters
                      </Text>
                    </FormControl>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      colorScheme="blue"
                      size="lg"
                      width="full"
                      isLoading={isSubmitting}
                      loadingText="Submitting..."
                      disabled={isSubmitting}
                      _hover={{
                        bg: 'blue.600',
                        transform: 'translateY(-1px)',
                      }}
                      transition="all 0.2s"
                    >
                      Submit Enquiry
                    </Button>
                  </VStack>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>

        {/* Information Card */}
        <Card bg="blue.50" borderRadius="lg">
          <CardBody>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold" mb={1}>
                  What happens next?
                </Text>
                <Text fontSize="sm">
                  After submitting your enquiry, our sales team will review your requirements and get back to you within 24 hours with detailed information about our products and services.
                </Text>
              </Box>
            </Alert>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default EnquiryForm;
